import { ResponseData } from "src/app/@types/interfaces/get.response";
import { CoreService } from "./core.service";
import timesheetData from "src/app/data/timesheet-data.json";
import { TimesheetEntry } from "src/app/@types/interfaces/timesheet.interface";

export interface TimesheetResponse {
  timesheet: TimesheetEntry[];
}

export default class TimesheetService extends CoreService {
  /**
   * Get all timesheet entries
   */
  async getAllTimesheets(): Promise<ResponseData<TimesheetResponse>> {
    return new Promise((resolve, reject) => {
      try {
        const response = {
          success: true,
          message: "Timesheet data retrieved successfully",
          data: timesheetData as TimesheetResponse,
        };
        resolve(response);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load timesheet data";
        reject(new Error(errorMessage));
      }
    });
  }

  /**
   * Get timesheet entries for a specific youth
   * @param youthId The ID of the youth
   */
  async getTimesheetsByYouthId(
    youthId: string
  ): Promise<ResponseData<TimesheetResponse>> {
    return new Promise((resolve, reject) => {
      try {
        const filteredTimesheet = timesheetData.timesheet.filter(
          (entry) => entry["Youth ID"] === youthId
        );

        const response = {
          success: true,
          message: `Timesheet data for youth ${youthId} retrieved successfully`,
          data: {
            timesheet: filteredTimesheet,
          } as TimesheetResponse,
        };
        resolve(response);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : `Failed to load timesheet data for youth ${youthId}`;
        reject(new Error(errorMessage));
      }
    });
  }

  /**
   * Get timesheet entries for a specific worksite
   * @param worksite The name of the worksite
   */
  async getTimesheetsByWorksite(
    worksite: string
  ): Promise<ResponseData<TimesheetResponse>> {
    return new Promise((resolve, reject) => {
      try {
        const filteredTimesheet = timesheetData.timesheet.filter(
          (entry) => entry.Worksite === worksite
        );

        const response = {
          success: true,
          message: `Timesheet data for worksite ${worksite} retrieved successfully`,
          data: {
            timesheet: filteredTimesheet,
          } as TimesheetResponse,
        };
        resolve(response);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : `Failed to load timesheet data for worksite ${worksite}`;
        reject(new Error(errorMessage));
      }
    });
  }

  /**
   * Get timesheet entries by batch ID
   * @param batchId The batch ID to filter by
   */
  async getTimesheetsByBatch(
    batchId: string
  ): Promise<ResponseData<TimesheetResponse>> {
    return new Promise((resolve, reject) => {
      try {
        const filteredTimesheet = timesheetData.timesheet.filter(
          (entry) => entry["Batch ID"] === batchId
        );

        const response = {
          success: true,
          message: `Timesheet data for batch ${batchId} retrieved successfully`,
          data: {
            timesheet: filteredTimesheet,
          } as TimesheetResponse,
        };
        resolve(response);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : `Failed to load timesheet data for batch ${batchId}`;
        reject(new Error(errorMessage));
      }
    });
  }

  /**
   * Get timesheet entries by status
   * @param status The status to filter by (e.g. "Approved", "App. Pending")
   */
  async getTimesheetsByStatus(
    status: string
  ): Promise<ResponseData<TimesheetResponse>> {
    return new Promise((resolve, reject) => {
      try {
        const filteredTimesheet = timesheetData.timesheet.filter(
          (entry) => entry.Status === status
        );

        const response = {
          success: true,
          message: `Timesheet data with status ${status} retrieved successfully`,
          data: {
            timesheet: filteredTimesheet,
          } as TimesheetResponse,
        };
        resolve(response);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : `Failed to load timesheet data with status ${status}`;
        reject(new Error(errorMessage));
      }
    });
  }
}

export const timesheetService = new TimesheetService();
